import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { TimeField } from '../ui/TimeField';
import { Select } from '../ui/Select';
import { useAuthStore } from '../../store/useAuthStore';
import { DAILY_STATUS_SETTINGS_KEY, useDailyStatusSettings } from '../../hooks/useDailyStatus';
import {
  pushReminderToApp,
  runDailyStatusNow,
  saveDailyStatusSettings,
  type DailyStatusLastRun,
} from '../../lib/dailyStatusApi';

/**
 * Settings → "Raport zilnic": when the reminder and the report go out, whether
 * the feature is on, who the automated chat reminder is sent from, plus the
 * scheduler's next/last runs and two "run it now" buttons.
 */
export function DailyStatusCard() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const settings = useDailyStatusSettings();
  const meId = useAuthStore((s) => s.user?.id);

  const [enabled, setEnabled] = useState(true);
  const [reminderTime, setReminderTime] = useState('17:30');
  const [reportTime, setReportTime] = useState('18:00');
  const [senderId, setSenderId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [lastManual, setLastManual] = useState<DailyStatusLastRun | null>(null);

  useEffect(() => {
    const cfg = settings.data?.config;
    if (!cfg) return;
    setEnabled(cfg.enabled);
    setReminderTime(cfg.reminderTime);
    setReportTime(cfg.reportTime);
    setSenderId(cfg.reminderSenderId ?? '');
  }, [settings.data]);

  async function save() {
    setSaving(true);
    setNotice(null);
    try {
      await saveDailyStatusSettings({
        enabled,
        reminderTime,
        reportTime,
        reminderSenderId: senderId || null,
      });
      await qc.invalidateQueries({ queryKey: DAILY_STATUS_SETTINGS_KEY });
      setNotice({ tone: 'ok', text: t('settings.dailyStatus.saved') });
    } catch (e) {
      setNotice({
        tone: 'error',
        text: e instanceof Error ? e.message : t('settings.unknownError'),
      });
    } finally {
      setSaving(false);
    }
  }

  async function pushToApp() {
    setRunning('push');
    setNotice(null);
    try {
      const result = await pushReminderToApp();
      setNotice({
        tone: 'ok',
        text: t('settings.dailyStatus.pushed', {
          count: result.usersWithPending,
          names: result.names.join(', ') || '—',
        }),
      });
      await qc.invalidateQueries({ queryKey: DAILY_STATUS_SETTINGS_KEY });
      await qc.invalidateQueries({ queryKey: ['daily-status', 'me'] });
    } catch (e) {
      setNotice({
        tone: 'error',
        text: e instanceof Error ? e.message : t('settings.unknownError'),
      });
    } finally {
      setRunning(null);
    }
  }

  async function run(kind: 'reminder' | 'report', onlyMe = false) {
    setRunning(onlyMe ? `${kind}:me` : kind);
    setNotice(null);
    try {
      const result = await runDailyStatusNow(kind, onlyMe ? meId : undefined);
      setLastManual(result);
      await qc.invalidateQueries({ queryKey: DAILY_STATUS_SETTINGS_KEY });
    } catch (e) {
      setNotice({
        tone: 'error',
        text: e instanceof Error ? e.message : t('settings.unknownError'),
      });
    } finally {
      setRunning(null);
    }
  }

  const when = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
  const schedule = settings.data?.schedule;
  const primary =
    'inline-flex items-center justify-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60';
  const secondary =
    'inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-60';

  function lastRunLine(label: string, run: DailyStatusLastRun | null) {
    if (!run) return null;
    return (
      <p className={run.ok ? '' : 'text-red-500'}>
        {label}: {when.format(new Date(run.at))} · {run.outcome}
        {run.summary ? ` · ${run.summary}` : ''}
        {run.errors.length > 0 ? ` · ${run.errors.join('; ')}` : ''}
      </p>
    );
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-card p-5">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-brand-600/15 p-2 text-brand-600">
          <ClipboardCheck className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{t('settings.dailyStatus.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('settings.dailyStatus.description')}</p>
        </div>
      </div>

      {settings.isLoading && (
        <p className="mt-4 text-sm text-gray-500">{t('settings.integrations.loading')}</p>
      )}

      {settings.data && (
        <form
          className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <label className="flex items-center gap-2 text-sm text-gray-800 sm:col-span-2">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4"
            />
            {t('settings.dailyStatus.enabled')}
          </label>
          <div>
            <label className="ui-field-label" htmlFor="ds-reminder">
              {t('settings.dailyStatus.reminderTime')}
            </label>
            <TimeField id="ds-reminder" value={reminderTime} onChange={setReminderTime} required />
          </div>
          <div>
            <label className="ui-field-label" htmlFor="ds-report">
              {t('settings.dailyStatus.reportTime')}
            </label>
            <TimeField id="ds-report" value={reportTime} onChange={setReportTime} required />
          </div>
          <div className="sm:col-span-2">
            <Select
              label={t('settings.dailyStatus.sender')}
              value={senderId}
              onChange={setSenderId}
              options={[
                { value: '', label: t('settings.dailyStatus.senderAuto') },
                ...settings.data.senderCandidates.map((u) => ({
                  value: u.id,
                  label: `${u.name} (${u.role})`,
                })),
              ]}
            />
            <p className="mt-1 text-xs text-gray-500">{t('settings.dailyStatus.senderHint')}</p>
          </div>
          <div className="flex flex-col gap-2 sm:col-span-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
            <button type="submit" disabled={saving} className={primary}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('settings.dailyStatus.save')}
            </button>
            <button
              type="button"
              disabled={running !== null}
              onClick={pushToApp}
              className={primary}
              title={t('settings.dailyStatus.pushHint')}
            >
              {running === 'push' && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('settings.dailyStatus.pushToApp')}
            </button>
            <button
              type="button"
              disabled={running !== null}
              onClick={() => run('reminder')}
              className={secondary}
            >
              {running === 'reminder' && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('settings.dailyStatus.runReminder')}
            </button>
            <button
              type="button"
              disabled={running !== null}
              onClick={() => run('report')}
              className={secondary}
            >
              {running === 'report' && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('settings.dailyStatus.runReport')}
            </button>
          </div>
          {/* Phones: these buttons stack full width (side by side the long
              labels were cut off mid-word). */}
          <div className="flex flex-col gap-2 sm:col-span-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
            <span className="text-xs text-gray-500">{t('settings.dailyStatus.testHint')}</span>
            <button
              type="button"
              disabled={running !== null || !meId}
              onClick={() => run('reminder', true)}
              className={secondary}
            >
              {running === 'reminder:me' && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('settings.dailyStatus.testReminder')}
            </button>
            <button
              type="button"
              disabled={running !== null || !meId}
              onClick={() => run('report', true)}
              className={secondary}
            >
              {running === 'report:me' && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('settings.dailyStatus.testReport')}
            </button>
          </div>
        </form>
      )}

      {notice && (
        <p className={`mt-3 text-sm ${notice.tone === 'ok' ? 'text-emerald-600' : 'text-red-600'}`}>
          {notice.text}
        </p>
      )}

      {schedule && (
        <div className="mt-4 space-y-0.5 text-xs text-gray-500">
          <p>
            {schedule.armed
              ? t('settings.dailyStatus.schedule', {
                  reminder: schedule.reminderTime,
                  report: schedule.reportTime,
                  zone: schedule.timeZone,
                })
              : t('settings.dailyStatus.scheduleOff')}
            {schedule.armed && schedule.nextReminderAt && (
              <>
                {' · '}
                {t('settings.dailyStatus.nextReminder', {
                  date: when.format(new Date(schedule.nextReminderAt)),
                })}
              </>
            )}
            {schedule.armed && schedule.nextReportAt && (
              <>
                {' · '}
                {t('settings.dailyStatus.nextReport', {
                  date: when.format(new Date(schedule.nextReportAt)),
                })}
              </>
            )}
          </p>
          {lastRunLine(t('settings.dailyStatus.lastReminder'), schedule.lastReminder)}
          {lastRunLine(t('settings.dailyStatus.lastReport'), schedule.lastReport)}
          {lastManual && lastRunLine(t('settings.dailyStatus.lastManual'), lastManual)}
        </div>
      )}
    </section>
  );
}
